import React, { useState, useEffect, useCallback } from "react";
import { fetchAuthSession } from 'aws-amplify/auth';
import mammoth from "mammoth";
import "./PageStyles.css";

// --- HTML Cleaning (mirrors the backend transformation) ---
const cleanHtmlContent = (raw) => {
  if (!raw) return "";
  let html = raw;

  // 1. Extract <body> content
  const lower = html.toLowerCase();
  const bodyOpen = lower.indexOf("<body");
  if (bodyOpen !== -1) {
    const bodyOpenEnd = html.indexOf(">", bodyOpen);
    const bodyClose = lower.lastIndexOf("</body>");
    if (bodyOpenEnd !== -1) {
      html =
        bodyClose !== -1 && bodyClose > bodyOpenEnd
          ? html.slice(bodyOpenEnd + 1, bodyClose).trim()
          : html.slice(bodyOpenEnd + 1).trim();
    }
  }

  // 2. Strip auxiliary tags
  html = html.replace(/<(head|meta|title|link|style|script)[^>]*>[\s\S]*?<\/\1>/gi, "");
  html = html.replace(/<\/?(html|head|body|meta|title|link|style|script)[^>]*>/gi, "");

  // 3. Strip the first <h1>
  html = html.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, "").trim();

  return html;
};

const AdminPage = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [type, setType] = useState("articles");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // Content management states
  const [articles, setArticles] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [games, setGames] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Preview/Editor states
  const [previewMode, setPreviewMode] = useState(false);
  const [editedHtml, setEditedHtml] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [isZip, setIsZip] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchContent = useCallback(async () => {
    setLoadingContent(true);
    try {
      const [articlesRes, tutorialsRes, gamesRes] = await Promise.all([
        fetch(`${apiUrl}/articles`),
        fetch(`${apiUrl}/tutorials`),
        fetch(`${apiUrl}/games`),
      ]);

      const articlesData = await articlesRes.json();
      const tutorialsData = await tutorialsRes.json();
      const gamesData = await gamesRes.json();

      setArticles(Array.isArray(articlesData) ? articlesData : JSON.parse(articlesData.body || "[]"));
      setTutorials(Array.isArray(tutorialsData) ? tutorialsData : JSON.parse(tutorialsData.body || "[]"));
      setGames(Array.isArray(gamesData) ? gamesData : JSON.parse(gamesData.body || "[]"));
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoadingContent(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // --- Preview: read the file client-side and show the editor ---
  const handlePreview = async () => {
    if (!file) {
      setMessage("Please select a file to preview.");
      setIsError(true);
      return;
    }
    if (!title.trim()) {
      setMessage("Please enter a title before previewing.");
      setIsError(true);
      return;
    }

    const ext = file.name.split(".").pop().toLowerCase();

    if (!["docx", "pdf", "html", "zip"].includes(ext)) {
      setMessage("Only .docx, .pdf, .html, and .zip files are supported.");
      setIsError(true);
      return;
    }

    setPreviewLoading(true);
    setMessage("Generating preview...");
    setIsError(false);

    try {
      if (ext === "pdf") {
        // PDF: show embedded viewer, no editor
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        setIsPdf(true);
        setIsZip(false);
        setEditedHtml("");
      } else if (ext === "zip") {
        setIsZip(true);
        setIsPdf(false);
        setEditedHtml("");
        setPdfUrl(null);
      } else if (ext === "html") {
        // HTML: read as text and clean
        const text = await readFileAsText(file);
        if (type === "games") {
          setEditedHtml(text);
        } else {
          const cleaned = cleanHtmlContent(text);
          setEditedHtml(cleaned);
        }
        setIsPdf(false);
        setIsZip(false);
        setPdfUrl(null);
      } else if (ext === "docx") {
        // DOCX: convert with mammoth
        const arrayBuffer = await readFileAsArrayBuffer(file);
        const result = await mammoth.convertToHtml({ arrayBuffer });
        const cleaned = cleanHtmlContent(result.value);
        setEditedHtml(cleaned);
        setIsPdf(false);
        setIsZip(false);
        setPdfUrl(null);
      }

      setPreviewMode(true);
      setMessage("");
    } catch (err) {
      console.error("Preview error:", err);
      setMessage(`Preview failed: ${err.message}`);
      setIsError(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const readFileAsText = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(f);
    });

  const readFileAsArrayBuffer = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(f);
    });

  const handleClosePreview = () => {
    setPreviewMode(false);
    setEditedHtml("");
    setIsPdf(false);
    setIsZip(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setMessage("");
  };

  // --- Post from preview (sends edited HTML directly) ---
  const handlePostFromPreview = async () => {
    if (isPdf || isZip || type === "games") {
      // PDF/ZIP/Games path: upload file via presigned URL
      await handleSubmit(null, true);
      return;
    }

    setPosting(true);
    setMessage("Posting content...");
    setIsError(false);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error("Not authenticated");

      const response = await fetch(`${apiUrl}/${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ title, edited_content: editedHtml }),
      });

      if (!response.ok) throw new Error("Processing failed");

      setMessage(`Successfully posted to ${type}!`);
      setTitle("");
      setDescription("");
      setFile(null);
      setPreviewMode(false);
      setEditedHtml("");
      fetchContent();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setIsError(true);
    } finally {
      setPosting(false);
    }
  };

  // --- Delete handler ---
  const handleDelete = async (id, contentType) => {
    const itemName = contentType === "articles" ? "article" : (contentType === "games" ? "game" : "tutorial");
    const confirmDelete = window.confirm(`Are you sure you want to delete this ${itemName}? This action cannot be undone.`);
    
    if (!confirmDelete) return;

    setDeletingId(id);
    setMessage(`Deleting ${itemName}...`);
    setIsError(false);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${apiUrl}/${contentType}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete: ${response.statusText}`);
      }

      setMessage(`Successfully deleted ${itemName}.`);
      fetchContent();
    } catch (error) {
      console.error("Delete error:", error);
      setMessage(`Error deleting: ${error.message}`);
      setIsError(true);
    } finally {
      setDeletingId(null);
    }
  };

  // --- Original submit flow (presigned URL upload) ---
  const handleSubmit = async (e, fromPreview = false) => {
    if (e) e.preventDefault();
    setMessage("Starting upload...");
    setIsError(false);

    if (!file) {
      setMessage("Please select a file.");
      setIsError(true);
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!["docx", "pdf", "html", "zip"].includes(fileExtension)) {
      setMessage("Only .docx, .pdf, .html, and .zip files are supported.");
      setIsError(true);
      return;
    }

    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setMessage(`File too large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Max limit is 20MB.`);
      setIsError(true);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error("Not authenticated");

      setMessage("Requesting upload permissions...");
      const presignedResponse = await fetch(`${apiUrl}/uploads/presigned-url?fileName=${encodeURIComponent(file.name)}&fileType=${encodeURIComponent(file.type)}`, {
        headers: { Authorization: token }
      });
      
      const { uploadUrl, key } = await presignedResponse.json();

      setMessage("Uploading file to storage...");
      await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      setMessage("Processing content...");
      const response = await fetch(`${apiUrl}/${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ title, file_key: key, file_type: fileExtension, description }),
      });

      if (!response.ok) throw new Error("Processing failed");

      setMessage(`Successfully posted to ${type}!`);
      setTitle("");
      setDescription("");
      setFile(null);
      if (fromPreview) {
        setPreviewMode(false);
      }
      fetchContent();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setIsError(true);
    }
  };

  // Character count for status bar
  const charCount = editedHtml.length;

  // ========================
  // RENDER: Preview/Editor Overlay
  // ========================
  if (previewMode) {
    return (
      <div className="preview-overlay">
        {/* Toolbar */}
        <div className="preview-toolbar">
          <div className="preview-toolbar-left">
            <span className="preview-toolbar-title" title={title}>{title}</span>
            <span className="preview-toolbar-type">
              {type === "articles" ? "Article" : (type === "games" ? "Game" : "Tutorial")}
            </span>
          </div>
          <div className="preview-toolbar-actions">
            <button className="preview-back-btn" onClick={handleClosePreview}>
              ← Back to Edit
            </button>
            <button
              className="preview-post-btn"
              onClick={handlePostFromPreview}
              disabled={posting}
            >
              {posting ? "Posting..." : "Post Content"}
            </button>
          </div>
        </div>

        {/* PDF: informational notice + embedded viewer */}
        {isPdf ? (
          <>
            <div style={{ padding: "1rem 1.5rem 0" }}>
              <div className="pdf-notice">
                <span className="pdf-notice-icon">⚠️</span>
                <div className="pdf-notice-text">
                  <strong>PDF files do not have first-class preview support.</strong>{" "}
                  The HTML editor is not available for PDFs. The backend will convert your
                  PDF to HTML automatically when posted. You can review the raw PDF below.
                </div>
              </div>
            </div>
            <div className="pdf-embed-container">
              <object data={pdfUrl} type="application/pdf" width="100%" height="100%">
                <div className="pdf-fallback">
                  <p>Your browser does not support embedded PDFs.</p>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#ff8c00" }}>
                    Open PDF in a new tab
                  </a>
                </div>
              </object>
            </div>
          </>
        ) : isZip ? (
          <div style={{ padding: "1rem 1.5rem 0", height: "100%" }}>
            <div className="pdf-notice" style={{ backgroundColor: "#1e3a8a", color: "#e0e7ff", borderLeftColor: "#3b82f6" }}>
              <span className="pdf-notice-icon">📦</span>
              <div className="pdf-notice-text">
                <strong>ZIP Preview is not available.</strong>{" "}
                Multi-file games inside ZIP archives cannot be previewed accurately in the admin console prior to upload.
                The backend will extract the game assets and make them available after you post.
              </div>
            </div>
          </div>

        ) : type === "games" && !isZip ? (
          <div className="preview-split-pane" style={{ gridTemplateColumns: "1fr" }}>
             <div className="preview-render-pane" style={{ width: "100%", borderLeft: "none" }}>
                <div className="preview-pane-header">
                  <span className="preview-pane-label">Game Preview</span>
                </div>
                <div className="preview-render-scroll" style={{ padding: 0 }}>
                  <iframe
                    title="Game Preview"
                    srcDoc={editedHtml}
                    sandbox="allow-scripts allow-same-origin"
                    style={{ width: "100%", height: "100%", border: "none", backgroundColor: "white" }}
                  />
                </div>
             </div>
          </div>
        ) : (
          /* HTML/DOCX: read-only live preview */
          <div className="preview-split-pane" style={{ gridTemplateColumns: "1fr" }}>
            <div className="preview-render-pane" style={{ width: "100%", maxWidth: "800px", margin: "0 auto", borderLeft: "none" }}>
              <div className="preview-pane-header">
                <span className="preview-pane-label">Read-Only Preview</span>
              </div>
              <div className="preview-render-scroll">
                <article className="full-article">
                  <h2>{title}</h2>
                  <span className="article-date">
                    Published on: {new Date().toLocaleDateString()}
                  </span>
                  <div
                    className="article-content"
                    dangerouslySetInnerHTML={{ __html: editedHtml }}
                  />
                </article>
              </div>
            </div>
          </div>
        )}

        {/* Status Bar */}
        <div className="preview-status-bar">
          <span className="preview-status-text">
            {isPdf
              ? "PDF preview"
              : isZip
              ? "ZIP preview unavailable"
              : `Read-Only Preview · ${charCount.toLocaleString()} characters`}
          </span>
          {message && (
            <div className={`message ${isError ? "error" : "success"}`}>
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================
  // RENDER: Normal Admin Form
  // ========================
  return (
    <div className="page-container">
      <h2>Admin Dashboard</h2>
      <p>Upload new content (DOCX, PDF, or HTML).</p>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
              <select
                className="form-select"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="articles">Article</option>
                <option value="tutorials">Tutorial</option>
                <option value="games">Game</option>
              </select>
        </div>

        <div className="form-group">
          <label>Title:</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Enter Title"
          />
        </div>

        {type === "games" && (
          <div className="form-group">
            <label>Description (Optional):</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a brief description of the game..."
              style={{
                width: "100%",
                padding: "0.75rem",
                backgroundColor: "#2a2a2a",
                border: "1px solid #444",
                borderRadius: "4px",
                color: "#e0e0e0",
                minHeight: "80px",
                fontFamily: "inherit",
                fontSize: "1rem"
              }}
            />
          </div>
        )}

        <div className="form-group">
          <label>Upload File (DOCX, PDF, or HTML) - Max 20MB:</label>
          <input
            type="file"
            accept=".docx, .pdf, .html"
            onChange={handleFileChange}
            required
          />
        </div>

        <div className="form-buttons-row">
          <button
            type="button"
            className="preview-btn"
            onClick={handlePreview}
            disabled={previewLoading || !file}
          >
            {previewLoading ? "Generating Preview..." : "👁 Preview & Edit"}
          </button>
          <button type="submit" className="submit-btn">
            Post Content
          </button>
        </div>

        {message && (
          <div className={`message ${isError ? "error" : "success"}`}>
            {message}
          </div>
        )}
      </form>

      {/* Content Management Section */}
      <section className="content-manager-section">
        <h3>Manage Existing Content</h3>
        {loadingContent ? (
          <p>Loading content list...</p>
        ) : (
          <div className="management-list">
            <h4>Articles</h4>
            {articles.length === 0 && <p>No articles found.</p>}
            {articles.map((item) => (
              <div key={item.article_id} className="management-item">
                <span>{item.title}</span>
                <button 
                  className="delete-btn"
                  disabled={deletingId === item.article_id}
                  onClick={() => handleDelete(item.article_id, "articles")}
                >
                  {deletingId === item.article_id ? "Deleting..." : "Delete"}
                </button>
              </div>
            ))}

            <h4 style={{ marginTop: '2rem' }}>Tutorials</h4>
            {tutorials.length === 0 && <p>No tutorials found.</p>}
            {tutorials.map((item) => (
              <div key={item.tutorial_id} className="management-item">
                <span>{item.title}</span>
                <button 
                  className="delete-btn"
                  disabled={deletingId === item.tutorial_id}
                  onClick={() => handleDelete(item.tutorial_id, "tutorials")}
                >
                  {deletingId === item.tutorial_id ? "Deleting..." : "Delete"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="content-manager-section">
        <h3>Manage Games</h3>
        {loadingContent ? (
          <p className="loading-text">Loading...</p>
        ) : games.length === 0 ? (
          <p className="empty-text">No games found.</p>
        ) : (
          <div className="management-list">
            {games.map((g) => (
              <div key={g.game_id} className="management-item">
                <span>{g.title}</span>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(g.game_id, "games")}
                  disabled={deletingId === g.game_id}
                >
                  {deletingId === g.game_id ? "Deleting..." : "Delete"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminPage;
